import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  Phone, 
  Calendar, 
  DollarSign, 
  User, 
  CheckCircle, 
  AlertCircle, 
  Clock,
  Save,
  ArrowLeft,
  Users,
  FileText,
  Edit
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { cobrancaService, clientesService } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';

const ControleCobranca = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Determinar o modo (criar, visualizar, editar)
  const isEditMode = window.location.pathname.includes('/editar');
  const isViewMode = id && !isEditMode;
  const isCreateMode = !id;
  
  // Detectar cliente pré-selecionado na URL
  const urlParams = new URLSearchParams(location.search);
  const clientePreSelecionado = urlParams.get('cliente');
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    cliente: '',
    status: 'cobrado',
    observacoes: '',
    data_promessa_pagamento: '',
    valor_prometido: '',
    forma_contato: '',
    proxima_cobranca: '',
    contas_ids: []
  });

  // Estados da interface
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [showContasSelection, setShowContasSelection] = useState(false);

  // Buscar clientes com contas em aberto (otimizado)
  const { data: clientes = [], isLoading: loadingClientes, error: clientesError } = useQuery({
    queryKey: ['clientes-cobranca'],
    queryFn: () => clientesService.comContasAbertas(10000),
    staleTime: 300000, // 5 minutos
    cacheTime: 600000, // 10 minutos
  });

  // Buscar cobrança existente (se estiver em modo de visualização/edição)
  const { data: cobrancaExistente, isLoading: loadingCobranca } = useQuery({
    queryKey: ['cobranca', id],
    queryFn: () => cobrancaService.getById(id),
    enabled: !!id,
    staleTime: 300000, // 5 minutos
  });

  // Buscar contas do cliente selecionado
  const { data: contasCliente = [], isLoading: loadingContas } = useQuery({
    queryKey: ['contas-cliente', selectedCliente?.id],
    queryFn: () => selectedCliente ? clientesService.contasAbertas(selectedCliente.id) : Promise.resolve([]),
    enabled: !!selectedCliente,
    staleTime: 60000, // 1 minuto
  });

  // Mutation para criar cobrança
  const createCobrancaMutation = useMutation({
    mutationFn: cobrancaService.create,
    onSuccess: () => {
      toast.success('Cobrança registrada com sucesso!');
      queryClient.invalidateQueries(['cobrancas']);
      navigate('/controle-cobranca');
    },
    onError: (error) => {
      toast.error(`Erro ao registrar cobrança: ${error.message}`);
    }
  });

  // Mutation para atualizar cobrança
  const updateCobrancaMutation = useMutation({
    mutationFn: (data) => cobrancaService.update(id, data),
    onSuccess: () => {
      toast.success('Cobrança atualizada com sucesso!');
      queryClient.invalidateQueries(['cobrancas']);
      queryClient.invalidateQueries(['cobranca', id]);
      navigate('/controle-cobranca');
    },
    onError: (error) => {
      toast.error(`Erro ao atualizar cobrança: ${error.message}`);
    }
  });

  // Carregar dados da cobrança existente
  useEffect(() => {
    if (cobrancaExistente) {
      setFormData({
        cliente: cobrancaExistente.cliente,
        status: cobrancaExistente.status,
        observacoes: cobrancaExistente.observacoes || '',
        data_promessa_pagamento: cobrancaExistente.data_promessa_pagamento || '',
        valor_prometido: cobrancaExistente.valor_prometido || '',
        forma_contato: cobrancaExistente.forma_contato || '',
        proxima_cobranca: cobrancaExistente.proxima_cobranca || '',
        contas_ids: cobrancaExistente.contas_cobranca?.map(cc => cc.conta) || []
      });
      
      // Buscar dados do cliente
      const cliente = clientes.find(c => c.id === cobrancaExistente.cliente);
      if (cliente) {
        setSelectedCliente(cliente);
        setShowContasSelection(true);
      }
    }
  }, [cobrancaExistente, clientes]);

  // Pré-selecionar cliente se vier da URL
  useEffect(() => {
    if (isCreateMode && clientePreSelecionado && clientes.length > 0) {
      const cliente = clientes.find(c => c.id === clientePreSelecionado);
      if (cliente) {
        setFormData(prev => ({ ...prev, cliente: cliente.id }));
        setSelectedCliente(cliente);
        setShowContasSelection(true);
      }
    }
  }, [clientePreSelecionado, clientes, isCreateMode]);

  // Handlers
  const handleClienteChange = (clienteId) => {
    const cliente = clientes.find(c => c.id === parseInt(clienteId));
    setSelectedCliente(cliente);
    setFormData(prev => ({ ...prev, cliente: clienteId, contas_ids: [] }));
    setShowContasSelection(true);
  };

  const handleContaToggle = (contaId) => {
    setFormData(prev => ({
      ...prev,
      contas_ids: prev.contas_ids.includes(contaId)
        ? prev.contas_ids.filter(id => id !== contaId)
        : [...prev.contas_ids, contaId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.cliente) {
      toast.error('Selecione um cliente');
      return;
    }

    if (formData.contas_ids.length === 0) {
      toast.error('Selecione pelo menos uma conta');
      return;
    }

    const dataToSubmit = {
      ...formData,
      cliente: parseInt(formData.cliente),
      valor_prometido: formData.valor_prometido ? parseFloat(formData.valor_prometido) : null,
      contas_ids: formData.contas_ids
    };

    if (isEditMode) {
      updateCobrancaMutation.mutate(dataToSubmit);
    } else {
      createCobrancaMutation.mutate(dataToSubmit);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Status options
  const statusOptions = [
    { value: 'nao_cobrado', label: 'Não Cobrado', icon: AlertCircle, color: 'text-gray-500' },
    { value: 'cobrado', label: 'Cobrado', icon: Phone, color: 'text-blue-600' },
    { value: 'promessa_pagamento', label: 'Promessa de Pagamento', icon: Calendar, color: 'text-orange-600' },
    { value: 'parcialmente_pago', label: 'Parcialmente Pago', icon: DollarSign, color: 'text-yellow-600' },
    { value: 'pago', label: 'Pago', icon: CheckCircle, color: 'text-green-600' },
  ];



  // Loading state para cobrança existente
  if (id && loadingCobranca) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/controle-cobranca')}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Carregando...</h1>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Carregando dados da cobrança...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
            {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/controle-cobranca')}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditMode ? 'Editar Cobrança' : isViewMode ? 'Detalhes da Cobrança' : 'Nova Cobrança'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isEditMode 
                ? 'Edite os dados da cobrança selecionada'
                : isViewMode 
                ? 'Visualize os detalhes da cobrança'
                : 'Registre uma nova cobrança para um cliente'
              }
            </p>
          </div>
        </div>
        {isViewMode && (
          <button
            onClick={() => navigate(`/controle-cobranca/${id}/editar`)}
            className="btn-primary flex items-center"
          >
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Seleção do Cliente */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Seleção do Cliente
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cliente *
              </label>
              {loadingClientes ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Carregando clientes...</p>
                </div>
              ) : clientesError ? (
                <div className="text-center py-4">
                  <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                  <p className="text-sm text-red-600 dark:text-red-400">Erro ao carregar clientes</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{clientesError.message}</p>
                </div>
              ) : (
                <div>
                  <select
                    value={formData.cliente}
                    onChange={(e) => handleClienteChange(e.target.value)}
                    className="input w-full"
                    required
                    disabled={isViewMode}
                  >
                    <option value="">Selecione um cliente ({clientes.length} disponíveis)</option>
                    {clientes.map(cliente => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nome_cliente} - {cliente.total_contas_abertas} contas em aberto
                      </option>
                    ))}
                  </select>

                </div>
              )}
            </div>

            {selectedCliente && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Informações do Cliente
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700 dark:text-blue-300">Nome:</span>
                    <span className="ml-2 text-blue-900 dark:text-blue-100">{selectedCliente.nome_cliente}</span>
                  </div>
                  <div>
                    <span className="text-blue-700 dark:text-blue-300">Contas em Aberto:</span>
                    <span className="ml-2 text-blue-900 dark:text-blue-100">{selectedCliente.total_contas_abertas}</span>
                  </div>
                  <div>
                    <span className="text-blue-700 dark:text-blue-300">Valor em Aberto:</span>
                    <span className="ml-2 text-blue-900 dark:text-blue-100">{formatCurrency(selectedCliente.total_valor_aberto)}</span>
                  </div>
                  <div>
                    <span className="text-blue-700 dark:text-blue-300">Inadimplência:</span>
                    <span className={`ml-2 font-medium ${
                      selectedCliente.percentual_inadimplencia > 80 ? 'text-red-600' :
                      selectedCliente.percentual_inadimplencia > 50 ? 'text-orange-600' :
                      selectedCliente.percentual_inadimplencia > 20 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {selectedCliente.percentual_inadimplencia}%
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Seleção de Contas */}
        {showContasSelection && (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Contas Tratadas nesta Cobrança
            </h2>
            
            {loadingContas ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">Carregando contas...</p>
              </div>
            ) : contasCliente.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">Nenhuma conta em aberto encontrada</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Selecione as contas que foram tratadas nesta cobrança:
                </p>
                
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {contasCliente.map(conta => (
                    <label
                      key={conta.id}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.contas_ids.includes(conta.id)
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.contas_ids.includes(conta.id)}
                        onChange={() => handleContaToggle(conta.id)}
                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {conta.nome_conta}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Vencimento: {formatDate(conta.vencimento_rec)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900 dark:text-white">
                              {formatCurrency(conta.valor_restante)}
                            </p>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              conta.esta_vencido ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300' :
                              conta.vence_em_breve ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300' :
                              'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                            }`}>
                              {conta.status_vencimento}
                            </span>
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {formData.contas_ids.length} conta(s) selecionada(s)
                </div>
              </div>
            )}
          </div>
        )}

        {/* Detalhes da Cobrança */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Detalhes da Cobrança
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="input"
                required
              >
                {statusOptions.map(option => {
                  const Icon = option.icon;
                  return (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Forma de Contato
              </label>
              <select
                value={formData.forma_contato}
                onChange={(e) => handleInputChange('forma_contato', e.target.value)}
                className="input"
              >
                <option value="">Selecione</option>
                <option value="telefone">Telefone</option>
                <option value="email">E-mail</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="pessoal">Pessoal</option>
                <option value="outro">Outro</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Data Prometida para Pagamento
              </label>
              <input
                type="date"
                value={formData.data_promessa_pagamento}
                onChange={(e) => handleInputChange('data_promessa_pagamento', e.target.value)}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Valor Prometido
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.valor_prometido}
                onChange={(e) => handleInputChange('valor_prometido', e.target.value)}
                className="input"
                placeholder="0,00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Próxima Cobrança
              </label>
              <input
                type="date"
                value={formData.proxima_cobranca}
                onChange={(e) => handleInputChange('proxima_cobranca', e.target.value)}
                className="input"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Observações
            </label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => handleInputChange('observacoes', e.target.value)}
              rows={4}
              className="input"
              placeholder="Descreva os detalhes da cobrança, acordos feitos, etc..."
            />
          </div>
        </div>

        {/* Botões */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/controle-cobranca')}
            className="btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={createCobrancaMutation.isPending}
            className="btn-primary inline-flex items-center"
          >
            {createCobrancaMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Registrar Cobrança
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ControleCobranca; 
